import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { uploadAttachment } from './attachments';
import {
  sendDurationMs,
  VoiceNoteRecorder,
  voiceNoteSupported,
  type VoiceNoteClip,
  type VoiceNoteErrorKind,
  type VoiceNoteState,
} from './voice-note-recorder';

// The composer's tap-to-record VOICE NOTE — the web half of the phones' `VoiceNoteRecorder` +
// `micTapped` flow. Record → the normal attachment upload → a normal send with EMPTY text and the
// audio attachment; the harness transcribes at the send seam and persists
// `<voice>{transcript}</voice>` (see orion-harness-poc `utils/voice-note.ts`). Distinct from
// dictation (`use-dictation`), which transcribes in the browser, sends TEXT, and throws the audio
// away: a voice note sends the audio, and the recipient can play it.
//
// The owning composer supplies `onSend` — what to do with the uploaded attachment — because the
// chat sends it into the open thread while the home launcher hands it to the chat it opens.

export type VoiceNoteErrorMessage = VoiceNoteErrorKind | 'send-failed';

/** What a composer needs to render + drive the recorder. Mirrors `Dictation`'s shape. */
export interface VoiceNoteComposer {
  /** The browser can record at all (resolved after mount; false during SSR). */
  supported: boolean;
  /** A take is in progress — the recorder bar replaces the composer's control row. */
  active: boolean;
  /** The clip is uploading/being sent: the bar stays up, its buttons go inert. */
  sending: boolean;
  /** Elapsed whole seconds, for the bar's timer. */
  seconds: number;
  /** Recent mic amplitudes in [0,1], newest last — the bar's waveform. */
  levels: number[];
  /** Which quiet inline notice to show, or null. */
  error: VoiceNoteErrorMessage | null;
  start: () => void;
  /** Stop + send (the bar's send button). */
  send: () => void;
  /** Throw the take away (the bar's trash). */
  discard: () => void;
  /** Re-upload a take whose send failed — the clip survives, so the words do too. */
  retry: () => void;
  dismissError: () => void;
}

/** The outgoing file name — `voice-note-<stamp>.m4a|webm`, the shape both phones use. */
export function voiceNoteFileName(mimeType: string, stamp: string): string {
  const extension = mimeType === 'audio/mp4' ? 'm4a' : 'webm';
  return `voice-note-${stamp}.${extension}`;
}

export function useVoiceNote({
  onSend,
  onUploadFailed,
  onStart,
  onDiscard,
  // Leaving the surface (a conversation switch, an unmount) throws an in-progress take away.
  resetKey,
}: {
  onSend: (attachment: MessageAttachment) => void;
  onUploadFailed?: (message: string, mimeType: string) => void;
  onStart?: () => void;
  onDiscard?: () => void;
  resetKey?: string | null;
}): VoiceNoteComposer {
  const recorder = useMemo(() => new VoiceNoteRecorder(), []);
  const [state, setState] = useState<VoiceNoteState>(() => recorder.getState());
  const [supported, setSupported] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  // Set by send() so the clip that lands is uploaded instead of sitting in preview: the phones have
  // no preview step — their send button stops the take and sends it — and web matches them.
  const pendingSendRef = useRef(false);
  // Bumped whenever the take in flight stops being the one we care about (discard, thread switch,
  // unmount, or a retry superseding an earlier attempt) so a late upload response is dropped.
  const generationRef = useRef(0);
  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;
  const onUploadFailedRef = useRef(onUploadFailed);
  onUploadFailedRef.current = onUploadFailed;

  useEffect(() => setSupported(voiceNoteSupported()), []);

  useEffect(() => {
    const unsubscribe = recorder.subscribe(setState);
    return () => {
      unsubscribe();
      recorder.cancel();
    };
  }, [recorder]);

  // A thread switch (or the composer going away) discards whatever was being recorded.
  useEffect(() => {
    return () => {
      pendingSendRef.current = false;
      // An upload still in flight must not resolve into the thread the user just left.
      generationRef.current += 1;
      recorder.cancel();
      setSending(false);
      setSendFailed(false);
    };
  }, [recorder, resetKey]);

  /**
   * Upload the stopped take and hand the attachment to the owner. Shared by the send that follows
   * a recording and by `retry()` — a failed upload KEEPS the clip (the user just spoke; throwing
   * the words away because the network blinked is the wrong answer), so the retry re-enters here
   * with the same clip rather than a second effect doing its own upload.
   *
   * `generationRef` is the stale-result guard: a discard, a thread switch or an unmount bumps it,
   * and a response from an older generation is dropped instead of sending a note the user has
   * already thrown away.
   */
  const uploadAndSend = useCallback(
    async (clip: VoiceNoteClip) => {
      // Too short to be anything but an accidental tap-tap, or an empty container: discard in
      // silence and restore the composer. The same gate, with the same constants, as both phones.
      const durationMs = sendDurationMs(clip.durationMs, clip.blob.size);
      if (durationMs === null) {
        recorder.cancel();
        return;
      }
      const generation = ++generationRef.current;
      setSending(true);
      setSendFailed(false);
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const file = new File([clip.blob], voiceNoteFileName(clip.mimeType, stamp), { type: clip.mimeType });
      const result = await uploadAttachment(file);
      if (generation !== generationRef.current) return;
      setSending(false);
      if (!result.ok) {
        // The clip stays in `preview` so Retry can re-upload it. `active` deliberately excludes
        // this case, so the composer swaps the recorder bar for the failure notice rather than
        // leaving a send button that can't fire.
        setSendFailed(true);
        onUploadFailedRef.current?.(result.message, clip.mimeType);
        return;
      }
      // Rebuilt to the wire shape, and stamped with the recorded duration (integer ms) so the
      // inline player shows the real length before it has decoded the file — as the phones do.
      onSendRef.current({
        fileName: result.attachment.fileName,
        mimeType: result.attachment.mimeType,
        url: result.attachment.url,
        ...(result.attachment.size !== undefined ? { size: result.attachment.size } : {}),
        durationMs,
      });
      recorder.cancel();
    },
    [recorder],
  );

  // The take stopped and a send was asked for → upload, then hand the attachment to the owner.
  useEffect(() => {
    if (state.status !== 'preview' || !state.clip || !pendingSendRef.current) return;
    pendingSendRef.current = false;
    void uploadAndSend(state.clip);
  }, [state.status, state.clip, uploadAndSend]);

  const start = useCallback(() => {
    setSendFailed(false);
    pendingSendRef.current = false;
    onStart?.();
    void recorder.start();
    // onStart is a BI ping supplied inline by the caller; re-running on its identity would
    // re-arm nothing. Deliberately keyed on the recorder alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder]);

  const send = useCallback(() => {
    if (recorder.getState().status !== 'recording') return;
    pendingSendRef.current = true;
    recorder.stop();
  }, [recorder]);

  const discard = useCallback(() => {
    pendingSendRef.current = false;
    generationRef.current += 1;
    setSendFailed(false);
    setSending(false);
    onDiscard?.();
    recorder.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorder]);

  // Re-upload the take a failed send left in `preview`. Same clip, same gate — nothing was
  // re-recorded, so the user's words survive a network blip.
  const retry = useCallback(() => {
    if (sending) return;
    const clip = recorder.getState().clip;
    if (!clip) {
      setSendFailed(false);
      return;
    }
    void uploadAndSend(clip);
  }, [recorder, sending, uploadAndSend]);

  const dismissError = useCallback(() => {
    generationRef.current += 1;
    setSendFailed(false);
    setSending(false);
    recorder.cancel();
  }, [recorder]);

  const error: VoiceNoteErrorMessage | null = sendFailed ? 'send-failed' : state.status === 'error' ? state.errorKind : null;

  return {
    supported,
    // 'starting' counts as active: the bar must be up the moment the mic is asked for, or the
    // composer flickers back between the permission prompt and the first sample.
    //
    // A FAILED send is deliberately NOT active even though the clip is still in `preview`: the
    // composer renders the recorder bar whenever it is, so leaving it active hides the failure
    // notice behind a bar whose send button cannot fire (the recorder is no longer `recording`).
    active: sending || state.status === 'starting' || state.status === 'recording' || (state.status === 'preview' && !sendFailed),
    sending,
    seconds: state.seconds,
    levels: state.levels,
    error,
    start,
    send,
    discard,
    retry,
    dismissError,
  };
}
